/* eslint-disable react/no-array-index-key */
import {
  AccordionDetails,
  Box,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useState, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';

import {
  convertCriteriaGroupActivity,
  StatusCode,
} from '../../../../../../api/enumerations';
import { deleteCriteriasGroup } from '../../../../../../api/theHive/criteriaGroups';
import {
  RemoveCriteriaGroupData,
  TypologyAndCriteriaData,
} from '../../../../../../api/theHive/criteriaGroups/types';
import {
  convertTypologyPropertyType,
  convertTypologyRuleKeyword,
  TypologyRuleKeyword,
} from '../../../../../../api/theHive/typology/enumerations';
import { ListTypologyData } from '../../../../../../api/theHive/typology/types';
import {
  IconExpandMoreMS,
  IconDeleteMS,
  IconKeyboardArrowRightMS,
  IconEditMS,
} from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import { formatReal } from '../../../../../../helpers';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { AddTypologyModal } from './components/AddTypologyModal';
import { EditGroupModal } from './components/EditGroupModal';
import { EditTypologyModal } from './components/EditTypologyModal';
import {
  CriteriasContainerGrid,
  NoContentText,
  StyledAccordionSummary,
  StyledAccordion,
  AccordionTitle,
  DisplacementText,
  DisplacementValue,
  ContentContainer as StyledContentContainer,
  ScrollableGrid,
  TypologyGroupName,
  AccordionCriteria,
  InfoBox,
  ItemTitle,
  EditTypologyGroupBox,
} from './styles';
import { TypologyGroupGridProps } from './types';

export function TypologyGroupSection({
  criteriasGroup,
  fetchTypologyGroups,
  isLoading,
}: TypologyGroupGridProps): JSX.Element {
  const [loadingOnDelete, setLoadingOnDelete] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState<number | null>(null);
  const [openCriteriaAccordionId, setOpenCriteriaAccordionId] = useState<
    number | null
  >(null);
  const [editTypologyGroup, setEditTypologyGroup] =
    useState<ListTypologyData>();
  const [editGroup, setEditGroup] = useState<TypologyAndCriteriaData>();

  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);
  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const scrollableGridRef = useRef<HTMLDivElement>(null);

  const formatArea = (
    ruleKeyword: TypologyRuleKeyword,
    area1: number | null,
    area2: number | null
  ): JSX.Element => {
    const translatedRuleKeyword = convertTypologyRuleKeyword(ruleKeyword);
    if (area1 && area2) {
      return (
        <>
          {translatedRuleKeyword} <DisplacementValue>{area1}</DisplacementValue>{' '}
          e<DisplacementValue>{area2}</DisplacementValue> m²
        </>
      );
    }
    return (
      <>
        {translatedRuleKeyword} <DisplacementValue>{area1}</DisplacementValue>{' '}
        m²
      </>
    );
  };

  const handleAccordionChange =
    (id: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? id : null);
    };

  const handleDeleteCriteriaGroup = async (
    criteriaGroupId: number
  ): Promise<void> => {
    try {
      setLoadingOnDelete(true);
      const data: RemoveCriteriaGroupData = {
        noticeId,
        criteriaGroupId,
      };
      const response = await deleteCriteriasGroup(data);

      if (response.detail.description)
        throw new Error(response.detail.description);
      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('Serviço excluído com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      fetchTypologyGroups();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    } finally {
      setLoadingOnDelete(false);
    }
  };

  if (isLoading || loadingOnDelete) {
    return (
      <CriteriasContainerGrid item container xs={12} mt={2}>
        <Grid item container>
          <Skeleton
            width={200}
            height={32}
            style={{
              backgroundColor: '#fafcff',
              boxShadow: '0px 3px 6px #00000029',
            }}
          />
        </Grid>
        <ScrollableGrid item container mt={2}>
          {[1, 2].map((_, index) => (
            <Skeleton
              style={{
                backgroundColor: '#fafcff',
                boxShadow: '0px 3px 6px #00000029',
              }}
              key={index}
              variant="rectangular"
              width="98%"
              height={45}
              sx={{ mb: 2, borderRadius: 1 }}
            />
          ))}
        </ScrollableGrid>
      </CriteriasContainerGrid>
    );
  }
  return (
    <>
      <CriteriasContainerGrid item container xs={12} mt={2}>
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize={21}>Grupos de serviço</Typography>
          <AddTypologyModal fetchTypologyGroups={fetchTypologyGroups} />
        </Grid>
        <ScrollableGrid item container mt={2} ref={scrollableGridRef}>
          {criteriasGroup?.length === 0 ? (
            <NoContentText>Nenhum serviço adicionado</NoContentText>
          ) : (
            criteriasGroup.map((group) => (
              <StyledAccordion
                key={group.id}
                expanded={expandedPanel === group.id}
                onChange={handleAccordionChange(group.id)}
              >
                <StyledAccordionSummary>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item container alignItems="center" xs spacing={1}>
                      <AccordionTitle>{group.name}</AccordionTitle>
                      <span className="expandIcon">{IconExpandMoreMS}</span>
                    </Grid>

                    <Grid item>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditGroup(group);
                        }}
                        color="primary"
                        title="editar grupo"
                        sx={{ '&:hover': { color: 'secondary.main' } }}
                      >
                        {IconEditMS}
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCriteriaGroup(group.id);
                        }}
                        color="primary"
                        title="excluir grupo"
                        sx={{ '&:hover': { color: 'secondary.main' } }}
                      >
                        {IconDeleteMS}
                      </IconButton>
                    </Grid>
                  </Grid>
                </StyledAccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <StyledContentContainer>
                    <InfoBox>
                      <DisplacementText>
                        • Valor:
                        <DisplacementValue>
                          {(group.typology_groups?.length ?? 0) > 0 &&
                            formatReal(group.typology_groups[0].price)}
                        </DisplacementValue>
                      </DisplacementText>
                      <DisplacementText>
                        • Conceito de espaço:
                        <DisplacementValue>
                          {convertCriteriaGroupActivity(group.activity)}
                        </DisplacementValue>
                      </DisplacementText>
                    </InfoBox>
                    <ItemTitle>Critérios:</ItemTitle>
                    {(group.criterias ?? []).map((criteria, index) => (
                      <InfoBox key={criteria.id}>
                        <AccordionCriteria
                          onClick={() => {
                            setOpenCriteriaAccordionId((prev) =>
                              prev === criteria.id ? null : criteria.id
                            );
                          }}
                        >
                          • Critério {index + 1}
                          {openCriteriaAccordionId === criteria.id
                            ? IconExpandMoreMS
                            : IconKeyboardArrowRightMS}
                        </AccordionCriteria>
                        {openCriteriaAccordionId === criteria.id && (
                          <Typography textAlign="justify" ml={2}>
                            {criteria.description}
                          </Typography>
                        )}
                      </InfoBox>
                    ))}
                    <ItemTitle>Tipologias:</ItemTitle>
                    {(group.typology_groups ?? []).map((typology) => (
                      <InfoBox
                        key={typology.id}
                        sx={{
                          '&:not(:last-child)': {
                            borderBottom: '1px solid #D7D8DB',
                            borderBottomStyle: 'dotted',
                          },
                          paddingBottom: '12px',
                        }}
                      >
                        <EditTypologyGroupBox>
                          <TypologyGroupName>
                            • {typology.name}
                          </TypologyGroupName>
                          <IconButton
                            onClick={() => setEditTypologyGroup(typology)}
                          >
                            {IconEditMS}
                          </IconButton>
                        </EditTypologyGroupBox>
                        {(typology.typologies ?? []).map((item) => (
                          <Box key={item.id} display="flex" gap="4px">
                            <DisplacementValue>
                              {convertTypologyPropertyType(
                                item.real_estate_type
                              )}
                            </DisplacementValue>
                            {formatArea(
                              item.rule_keyword,
                              item.rule_first_value,
                              item.rule_second_value || null
                            )}
                          </Box>
                        ))}
                      </InfoBox>
                    ))}
                  </StyledContentContainer>
                </AccordionDetails>
              </StyledAccordion>
            ))
          )}
        </ScrollableGrid>
      </CriteriasContainerGrid>
      {editTypologyGroup && (
        <EditTypologyModal
          fetchTypologyGroups={fetchTypologyGroups}
          typologyGroup={editTypologyGroup}
          open={!!editTypologyGroup}
          handleClose={() => {
            setEditTypologyGroup(undefined);
          }}
        />
      )}
      {editGroup && (
        <EditGroupModal
          fetchTypologyGroups={fetchTypologyGroups}
          group={editGroup}
          open={!!editGroup}
          handleClose={() => {
            setEditGroup(undefined);
          }}
        />
      )}
    </>
  );
}
